import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { PropsWithChildren } from 'react';
import {
  getSession,
  isAuthenticated,
  login,
  loginChallenge,
  partnerLogin,
  partnerLoginChallenge,
  partnerRefreshSession,
  logout as apiLogout,
  clearSession,
  saveSessionFromTokens,
} from '../../lib/identityAuth';
import type { AuthChallenge, AuthSession } from '../../types/auth';

interface AuthContextValue {
  session: AuthSession | null;
  challenge: AuthChallenge | null;
  isLoggedIn: boolean;
  login: (email: string, password: string) => Promise<{ challenge?: AuthChallenge }>;
  partnerLogin: (email: string, password: string) => Promise<{ challenge?: AuthChallenge }>;
  completeNewPassword: (email: string, newPassword: string) => Promise<void>;
  completePartnerNewPassword: (email: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Clear portal storage + React state without Cognito GlobalSignOut (caller handoff). */
  discardLocalSession: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const REFRESH_SKEW_MS = 60_000;

export function AuthProvider({ children }: PropsWithChildren): JSX.Element {
  const [session, setSession] = useState<AuthSession | null>(() => getSession());
  const [challenge, setChallenge] = useState<AuthChallenge | null>(null);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await login(email, password);

    if (result.challenge && result.session) {
      const pending: AuthChallenge = {
        challenge: result.challenge,
        session: result.session,
        params: result.challenge_parameters,
      };
      setChallenge(pending);
      return { challenge: pending };
    }

    if (result.tokens) {
      const updated = saveSessionFromTokens(result.tokens, { authSurface: 'general' });
      setChallenge(null);
      setSession(updated);
      return {};
    }

    throw new Error('Unexpected login response');
  }, []);

  const partnerSignIn = useCallback(async (email: string, password: string) => {
    const result = await partnerLogin(email, password);

    if (result.challenge && result.session) {
      const pending: AuthChallenge = {
        challenge: result.challenge,
        session: result.session,
        params: result.challenge_parameters,
      };
      setChallenge(pending);
      return { challenge: pending };
    }

    if (result.tokens) {
      const updated = saveSessionFromTokens(result.tokens, { authSurface: 'partner' });
      setChallenge(null);
      setSession(updated);
      return {};
    }

    throw new Error('Unexpected partner login response');
  }, []);

  const completeNewPassword = useCallback(
    async (email: string, newPassword: string) => {
      if (!challenge) {
        throw new Error('No pending password challenge');
      }

      const result = await loginChallenge(email, challenge.session, newPassword);

      if (result.challenge && result.session) {
        setChallenge({
          challenge: result.challenge,
          session: result.session,
        });
        return;
      }

      if (result.tokens) {
        const updated = saveSessionFromTokens(result.tokens, { authSurface: 'general' });
        setSession(updated);
        setChallenge(null);
        return;
      }

      throw new Error('Unexpected challenge response');
    },
    [challenge],
  );

  const completePartnerNewPassword = useCallback(
    async (email: string, newPassword: string) => {
      if (!challenge) {
        throw new Error('No pending password challenge');
      }

      const result = await partnerLoginChallenge(email, challenge.session, newPassword);

      if (result.challenge && result.session) {
        setChallenge({
          challenge: result.challenge,
          session: result.session,
        });
        return;
      }

      if (result.tokens) {
        const updated = saveSessionFromTokens(result.tokens, { authSurface: 'partner' });
        setSession(updated);
        setChallenge(null);
        return;
      }

      throw new Error('Unexpected partner challenge response');
    },
    [challenge],
  );

  const logout = useCallback(async () => {
    await apiLogout();
    setSession(null);
    setChallenge(null);
  }, []);

  const discardLocalSession = useCallback(() => {
    clearSession();
    setSession(null);
    setChallenge(null);
  }, []);

  // Partner silent refresh until logout or refresh-token expiry.
  useEffect(() => {
    if (!session || session.authSurface !== 'partner' || !session.refreshToken) {
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = () => {
      const delay = Math.max(5_000, session.expiresAt - Date.now() - REFRESH_SKEW_MS);
      timer = setTimeout(async () => {
        try {
          const updated = await partnerRefreshSession();
          if (!cancelled) setSession(updated);
        } catch {
          if (!cancelled) {
            clearSession();
            setSession(null);
          }
        }
      }, delay);
    };

    schedule();

    const onVisibility = () => {
      if (document.visibilityState !== 'visible' || cancelled) return;
      if (Date.now() >= session.expiresAt - REFRESH_SKEW_MS) {
        void partnerRefreshSession()
          .then((updated) => {
            if (!cancelled) setSession(updated);
          })
          .catch(() => {
            if (!cancelled) {
              clearSession();
              setSession(null);
            }
          });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [session]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      challenge,
      isLoggedIn: Boolean(session && isAuthenticated()),
      login: signIn,
      partnerLogin: partnerSignIn,
      completeNewPassword,
      completePartnerNewPassword,
      logout,
      discardLocalSession,
    }),
    [
      challenge,
      completeNewPassword,
      completePartnerNewPassword,
      discardLocalSession,
      logout,
      partnerSignIn,
      session,
      signIn,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
